import { Link, Outlet } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Home } from 'lucide-react';
import {
  ContactValue,
  DetailList,
  PageHeader,
  Section,
  StatusStrip,
  Tabs,
  pageStyles as s,
} from '../../components/page.tsx';
import { buttonClass, ComingSoon, EmptyState, Panel, Splash } from '../../components/ui.tsx';
import { propertyTitle, useLandlords } from '../../lib/properties.ts';
import { companyRows, homeRows, sharedOnly } from '../shared/propertyRows.tsx';
import { ContactsList } from '../shared/ContactsList.tsx';
import { FileGroups, FileList } from '../shared/Files.tsx';
import { LeaseInspections } from '../shared/InspectionView.tsx';
import { MaintenanceLog } from '../shared/Maintenance.tsx';
import { useInspections } from '../../lib/inspections.ts';
import { useThreadCounts } from '../../lib/messages.ts';
import { LeaseParties, LeaseTerms } from '../shared/LeaseView.tsx';
import { activeLease, useFiles, useLeases } from '../../lib/tenancy.ts';
import { formatCents, formatDate, nextDueDate, todayIso } from '@vuokramani/shared';
import { useTenantHistory } from '../../lib/history.ts';
import { useTenant } from './TenantShell.tsx';

/** Vuokralaisen asunto: tilanne ylhäällä, välilehdet Tiedot, Sopimus, Tiedostot, Muuttotarkastus, Huolto. */
export function TenantPropertyPage() {
  const { t, i18n } = useTranslation();
  const tenant = useTenant();
  const { current, isLoading } = tenant;
  const { data: leases } = useLeases(current?.id);
  const lease = activeLease(leases);
  const { data: inspections } = useInspections(lease?.id);
  const moveIn = inspections?.find((i) => i.type === 'move_in');
  const moveOut = inspections?.find((i) => i.type === 'move_out');
  const { data: counts } = useThreadCounts('party');
  const { data: history, isLoading: historyLoading } = useTenantHistory();

  if (isLoading || (!current && historyLoading)) return <Splash />;
  if (!current) {
    // Entinen vuokralainen: vuokrasuhde päättyi, aineisto löytyy historiasta.
    const last = history?.[0];
    if (last) {
      return (
        <EmptyState
          icon={<Home size={22} />}
          title={t('tenant.endedTitle')}
          text={t('tenant.endedText', {
            property: last.propertyTitle,
            date: last.endDate ? formatDate(last.endDate, i18n.language) : '–',
          })}
          action={
            <Link to="/vuokralainen/historia" className={buttonClass('primary')}>
              {t('tenant.openHistory')}
            </Link>
          }
        />
      );
    }
    return (
      <EmptyState
        icon={<Home size={22} />}
        title={t('tenant.propertyTitle')}
        text={t('tenant.noProperty')}
      />
    );
  }

  const base = '/vuokralainen/asunto';
  return (
    <div className={s.stack} style={{ gap: '1.5rem' }}>
      <PageHeader
        title={propertyTitle(current)}
        subtitle={`${current.streetAddress}, ${current.postalCode} ${current.city}`}
      />
      {lease?.terminatedBy && lease.endDate ? (
        <div className={s.banner} role="status">
          {/* Lopputarkastuksen tekee vuokranantaja; valmis näkyy vuokralaiselle. */}
          <span>
            {t(
              `tenant.${moveOut?.status === 'approved' ? 'endingDone' : 'ending'}${
                lease.endDate < todayIso() ? 'Past' : ''
              }`,
              { date: formatDate(lease.endDate, i18n.language) },
            )}
          </span>
          {moveOut?.status === 'approved' && (
            <Link to={`${base}/tarkastus`} className={buttonClass('secondary')}>
              {t('tenant.endingOpen')}
            </Link>
          )}
        </div>
      ) : (
        lease &&
        lease.moveInInspection === 'app' &&
        lease.tenantInspections &&
        (!moveIn || moveIn.status === 'draft') && (
          <div className={s.banner} role="status">
            <span>
              {moveIn?.returnComment
                ? t('tenant.inspectionReturned')
                : moveIn?.dueDate
                  ? t('tenant.inspectionDue', { date: formatDate(moveIn.dueDate, i18n.language) })
                  : t('tenant.inspectionTodo')}
            </span>
            <Link to={`${base}/tarkastus`} className={buttonClass('secondary')}>
              {t('tenant.inspectionOpen')}
            </Link>
          </div>
        )
      )}
      <StatusStrip
        items={[
          // Seuraava vuokra vain, jos vuokrasuhde on voimassa seuraavana eräpäivänä.
          ...(!lease
            ? [{ label: t('tenant.nextRent'), value: t('tenant.noInvoices'), muted: true }]
            : lease.endDate && lease.endDate < nextDueDate(lease.dueDay)
              ? []
              : [
                  {
                    label: t('tenant.nextRentOn', {
                      date: formatDate(nextDueDate(lease.dueDay), i18n.language),
                    }),
                    value: formatCents(lease.rentCents, i18n.language),
                  },
                ]),
          // Maksamatta = eräpäivä ohi; vain jos laskut ovat palvelussa ja näkyvät vuokralaiselle.
          ...(lease?.hasInvoices && current.settings.tenantInvoices
            ? [
                {
                  label: t('tenant.unpaid'),
                  value: formatCents(lease.overdueCents, i18n.language),
                  muted: lease.overdueCents === 0,
                },
              ]
            : []),
          {
            label: t('tenant.openFaults'),
            value: counts?.openFaults ?? 0,
            muted: !counts?.openFaults,
          },
        ]}
      />
      <div>
        <Tabs
          label={t('tenant.propertyTitle')}
          items={[
            { to: base, label: t('tenant.tabs.details'), end: true },
            { to: `${base}/sopimus`, label: t('tenant.tabs.contract') },
            { to: `${base}/tiedostot`, label: t('tenant.tabs.files') },
            { to: `${base}/tarkastus`, label: t('tenant.tabs.inspection') },
            { to: `${base}/huolto`, label: t('tenant.tabs.maintenance') },
          ]}
        />
        <Outlet context={tenant} />
      </div>
    </div>
  );
}

/** Vuokralaisen tiedot: samat jaetut rivit kuin vuokranantajalla, sekä vuokranantajan yhteystiedot. */
export function TenantDetailsTab() {
  const { t, i18n } = useTranslation();
  const { current: p } = useTenant();
  const { data: landlords } = useLandlords(p?.id);
  const { data: leases } = useLeases(p?.id);
  const lease = activeLease(leases);
  if (!p) return null;
  return (
    <div className={s.stack}>
      <Section title={t('property.sections.home')}>
        <Panel>
          <DetailList rows={sharedOnly(homeRows(p, t, i18n.language))} />
        </Panel>
      </Section>
      <Section title={t('property.sections.company')}>
        <Panel>
          <DetailList rows={sharedOnly(companyRows(p, t))} />
          <ContactsList propertyId={p.id} editable={false} />
        </Panel>
      </Section>
      <Section title={t('property.fields.landlord')}>
        <Panel>
          <DetailList
            rows={(landlords ?? []).map((l, i) => ({
              label: i === 0 ? t('property.fields.landlord') : '',
              value: (
                <ContactValue
                  name={l.name ?? t('property.noName')}
                  phone={l.phone}
                  email={l.email}
                />
              ),
            }))}
          />
        </Panel>
      </Section>
      <Section title={t('property.tenancyTitle')}>
        {lease ? <LeaseTerms lease={lease} /> : <ComingSoon text={t('tenant.tenancySoon')} />}
      </Section>
    </div>
  );
}

/** Sopimus: ehdot, osapuolet ja allekirjoitettu sopimus tiedostona. */
export function TenantContractTab() {
  const { t } = useTranslation();
  const { current: p } = useTenant();
  const { data: leases } = useLeases(p?.id);
  const { data: files } = useFiles(p?.id);
  const lease = activeLease(leases);
  if (!p) return null;
  const contracts = (files ?? []).filter((f) => f.folder === 'contract');
  if (!lease) return <ComingSoon text={t('tenant.tenancySoon')} />;
  return (
    <div className={s.stack}>
      <Section title={t('lease.contractFile')}>
        {contracts.length > 0 ? (
          <Panel>
            <FileList files={contracts} propertyId={p.id} editable={false} />
          </Panel>
        ) : (
          <p style={{ color: 'var(--muted)' }}>{t('lease.noContractFile')}</p>
        )}
      </Section>
      <Section title={t('lease.terms')}>
        <LeaseTerms lease={lease} />
      </Section>
      <Section title={t('lease.parties')}>
        <LeaseParties lease={lease} />
      </Section>
    </div>
  );
}

/** Vuokranantajan jakamat asiakirjat kansioittain. */
export function TenantFilesTab() {
  const { t } = useTranslation();
  const { current: p } = useTenant();
  const { data: files } = useFiles(p?.id);
  if (!p || !files) return null;
  return files.length > 0 ? (
    <FileGroups files={files} propertyId={p.id} editable={false} />
  ) : (
    <ComingSoon text={t('tenant.noFiles')} />
  );
}

export function TenantInspectionTab() {
  const { t } = useTranslation();
  const { current: p } = useTenant();
  const { data: leases, isLoading } = useLeases(p?.id);
  if (!p || isLoading) return null;
  const lease = activeLease(leases);
  return lease ? <LeaseInspections lease={lease} /> : <ComingSoon text={t('tenant.tenancySoon')} />;
}

/** Vuokranantajan jakamat huoltokirjan tapahtumat, ilman hintoja. */
export function TenantMaintenanceTab() {
  const { t } = useTranslation();
  const { current: p } = useTenant();
  if (!p) return null;
  return (
    <MaintenanceLog propertyId={p.id} editable={false} emptyText={t('tenant.noMaintenance')} />
  );
}
